import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, CreditCard, Building2, Check,
  FileText, Download, Mail, Loader2, AlertCircle, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { billingApi, dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import { MONTHLY_PER_USER, ANNUAL_PER_USER, ANNUAL_SAVING, ANNUAL_DISCOUNT_PERCENT } from '../lib/pricing';

const BillingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for payment status from URL
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment cancelled');
    }
  // Runs once on mount. `checkPaymentStatus` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billingRes, invoicesRes, approvalsRes] = await Promise.all([
        billingApi.getOverview(),
        billingApi.getInvoices(),
        dashboardApi.getPendingApprovals()
      ]);
      
      setBilling(billingRes.data);
      setInvoices(invoicesRes.data);
      setPendingCount(approvalsRes.data.length);
      setInvoiceEmail(billingRes.data.invoice_email || '');
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const pollStatus = async () => {
      try {
        const response = await billingApi.getCheckoutStatus(sessionId);
        
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Your subscription is now active.');
          fetchData();
          // Clear URL params
          navigate('/settings/billing', { replace: true });
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          navigate('/settings/billing', { replace: true });
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          toast.info('Payment is being processed. Please check back shortly.');
          navigate('/settings/billing', { replace: true });
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        toast.error('Error checking payment status');
      } finally {
        if (attempts >= maxAttempts || checkingPayment === false) {
          setCheckingPayment(false);
        }
      }
    };
    
    pollStatus();
  };

  const handleStripeCheckout = async (planType) => {
    try {
      const response = await billingApi.createStripeCheckout(planType);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to initiate payment. Please try again.');
    }
  };

  const handleGoCardlessSetup = async (planType) => {
    try {
      const response = await billingApi.setupGoCardless(planType);
      
      if (response.data.status === 'not_configured') {
        toast.info(response.data.message);
      } else if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      console.error('Failed to setup Direct Debit:', error);
      toast.error('Failed to setup Direct Debit. Please try again.');
    }
  };

  const handleSaveInvoiceEmail = async () => {
    if (!invoiceEmail) return;
    
    setSavingEmail(true);
    try {
      await billingApi.updateInvoiceEmail(invoiceEmail);
      toast.success('Invoice email updated');
    } catch (error) {
      console.error('Failed to update email:', error);
      toast.error('Failed to update invoice email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getDaysRemaining = (trialEndDate) => {
    if (!trialEndDate) return 0;
    const end = new Date(trialEndDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl font-bold">Billing</h1>
                <p className="text-sm text-muted-foreground">Manage your subscription and payment methods</p>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6 max-w-4xl mx-auto space-y-6">
          {/* Trial Banner */}
          {billing?.is_trial && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-4">
              <AlertCircle className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-primary">
                  You have {getDaysRemaining(billing.trial_ends_at)} days left in your free trial
                </p>
                <p className="text-sm text-muted-foreground">
                  Subscribe now to continue using HolidayHive after your trial ends.
                </p>
              </div>
            </div>
          )}

          {/* Checking Payment Status */}
          {checkingPayment && (
            <div className="bg-secondary/50 border border-border rounded-lg p-4 flex items-center gap-4">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <p className="font-medium">Checking payment status...</p>
            </div>
          )}

          {/* Your Plan */}
          <div className="card-hive p-6">
            <h2 className="font-heading text-xl font-bold mb-4">Your Plan</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Monthly Plan */}
              <div className={`border rounded-lg p-4 ${billing?.plan_type === 'monthly' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="bg-primary/20 text-primary text-xs font-bold px-2 py-1 rounded">MONTHLY</span>
                  <span className="font-bold text-lg">{formatCurrency(MONTHLY_PER_USER)}/user</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Pay monthly, cancel anytime. All features included.
                </p>
                <div className="text-sm mb-4">
                  <span className="text-muted-foreground">Your cost: </span>
                  <span className="font-bold">{formatCurrency(billing?.monthly_cost || 0)}/month</span>
                  <span className="text-muted-foreground"> ({billing?.user_count} users)</span>
                </div>
                {billing?.plan_type === 'monthly' && billing?.has_payment_method ? (
                  <div className="flex items-center gap-2 text-primary">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">Current Plan</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button 
                      className="w-full btn-primary" 
                      onClick={() => handleStripeCheckout('monthly')}
                      data-testid="monthly-card-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay with Card
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => handleGoCardlessSetup('monthly')}
                      data-testid="monthly-dd-btn"
                    >
                      <Banknote className="w-4 h-4 mr-2" />
                      Pay with Direct Debit
                    </Button>
                  </div>
                )}
              </div>

              {/* Annual Plan */}
              <div className={`border rounded-lg p-4 ${billing?.plan_type === 'annual' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="bg-green-500/20 text-green-500 text-xs font-bold px-2 py-1 rounded">ANNUAL</span>
                    <span className="bg-amber-500/20 text-amber-500 text-xs font-medium px-2 py-1 rounded">{ANNUAL_SAVING}</span>
                  </div>
                  <span className="font-bold text-lg">{formatCurrency(ANNUAL_PER_USER)}/user</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Pay for the year up front and save {ANNUAL_DISCOUNT_PERCENT}%. All features included.
                </p>
                <div className="text-sm mb-4">
                  <span className="text-muted-foreground">Your cost: </span>
                  <span className="font-bold">{formatCurrency(billing?.annual_cost || 0)}/year</span>
                  <span className="text-muted-foreground"> ({billing?.user_count} users)</span>
                  <div className="text-green-500 text-xs mt-1">
                    Save {formatCurrency(billing?.annual_savings || 0)} per year
                  </div>
                </div>
                {billing?.plan_type === 'annual' && billing?.has_payment_method ? (
                  <div className="flex items-center gap-2 text-primary">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">Current Plan</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button 
                      className="w-full btn-primary" 
                      onClick={() => handleStripeCheckout('annual')}
                      data-testid="annual-card-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay with Card
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => handleGoCardlessSetup('annual')}
                      data-testid="annual-dd-btn"
                    >
                      <Banknote className="w-4 h-4 mr-2" />
                      Pay with Direct Debit
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Billing Details */}
          <div className="card-hive p-6">
            <h2 className="font-heading text-xl font-bold mb-4">Billing Details</h2>
            <p className="text-sm text-muted-foreground mb-4">
              In here you'll find all your plan details, billing info and payment methods.
            </p>
            
            {billing?.has_payment_method ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {billing.payment_method === 'stripe' ? (
                      <CreditCard className="w-5 h-5 text-primary" />
                    ) : (
                      <Banknote className="w-5 h-5 text-primary" />
                    )}
                    <div>
                      <p className="font-medium">
                        {billing.payment_method === 'stripe' ? 'Card Payment' : 'Direct Debit'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Next billing: {billing.next_billing_date 
                          ? new Date(billing.next_billing_date).toLocaleDateString('en-GB', { 
                              day: 'numeric', month: 'long', year: 'numeric' 
                            })
                          : 'Not set'}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Update
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No payment method configured</p>
                <p className="text-sm">Choose a plan above to set up your payment method</p>
              </div>
            )}
          </div>

          {/* Your Invoices */}
          <div className="card-hive p-6">
            <h2 className="font-heading text-xl font-bold mb-4">Your Invoices</h2>
            
            <div className="mb-4">
              <Label className="text-sm text-muted-foreground">Send a copy of your invoices to</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  placeholder="Invoice email address"
                  value={invoiceEmail}
                  onChange={(e) => setInvoiceEmail(e.target.value)}
                  className="input-hive max-w-md"
                  data-testid="invoice-email-input"
                />
                <Button 
                  variant="outline" 
                  onClick={handleSaveInvoiceEmail}
                  disabled={savingEmail || !invoiceEmail}
                  data-testid="save-email-btn"
                >
                  {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
            
            {invoices.length > 0 ? (
              <div className="space-y-2">
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{invoice.invoice_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {invoice.issue_date && new Date(invoice.issue_date).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm px-2 py-1 rounded ${
                        invoice.status === 'paid' 
                          ? 'bg-green-500/20 text-green-500' 
                          : 'bg-amber-500/20 text-amber-500'
                      }`}>
                        {invoice.status}
                      </span>
                      <span className="font-medium">{formatCurrency(invoice.amount)}</span>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No invoices yet</p>
                <p className="text-sm">Your invoices will appear here after you subscribe</p>
              </div>
            )}
            
            {invoices.length > 0 && (
              <button className="mt-4 text-primary hover:underline text-sm flex items-center gap-1">
                <FileText className="w-4 h-4" />
                View and download all invoices
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BillingPage;
